<template>
  <div class="user-short-container" :class="{ truncate: truncateName }">
    <div
      class="user-photo-container rounded-circle"
      :class="{ [mode]: true, 'no-user': !userId, 'no-default-avatar': hideDefaultAvatar }"
      :title="showTooltipOnAvatar ? user.displayName : ''"
      :style="{ width: length + 'px', height: length + 'px' }"
      v-no-capture
      v-if="showAvatar"
    >
      <img
        class="user-photo rounded-circle"
        :src="photoUrl"
        @error="isErrored = true"
        v-if="userId && photoUrl && !isErrored"
      />
      <div
        v-else-if="userId && user.displayName"
        class="user-initials flex-center rounded-circle position-relative"
        :style="{
          'font-size': Math.round(length * 0.42) + 'px',
          background: initialsBgColor(userId)
        }"
      >
        <div class="position-absolute">{{ initials }}</div>
      </div>
      <slot></slot>
    </div>
    <span
      :class="{ 'text-truncate': truncateName, bold: isNameBold, 'ml-1': showAvatar }"
      v-if="showUserName && user.displayName"
      class="user-name"
      v-no-capture
    >
      {{ user.displayName }}
    </span>
  </div>
</template>

<script>
/**
 * This component shows short avatar of a user.
 * We can pass-in the userId, and it will display corresponding user's avatar
 * Props:
 *  - userId
 *  - length: This is the size of avatar in pixel
 *  - showUserName: Whether to display user name
 *  - mode
 *    Acceptable values:
 *      - edit-view: It shows default blue avatar with dashed border around it
 *      - read-view: this is the default view
 */

import getInitials from '@/utils/getInitials'
import { PALETTE } from '@/constants/common'
import getPhotoUrl from '@/utils/getPhotoUrl'

/**
 * This shows user's avatar, and name. Name is by default not shown. Use prop - `showUserName` to show name
 */
export default {
  name: 'UserShort',
  props: {
    userId: [Number, String],
    /**
     * Diameter(in pixels) of avatar
     */
    length: [Number, String],
    showUserName: Boolean,
    /**
     * Acceptable values:
     *  - edit-view: It shows default blue avatar with dashed border around it
     *  - read-view: this is the default view
     */
    mode: { type: String, default: 'read-view' },
    truncateName: { type: Boolean, default: false },
    isNameBold: { type: Boolean, default: false },
    showTooltipOnAvatar: { type: Boolean, default: false },
    /**
     * By default, a default avatar is shown when userId is not present. Set this flag to not show default avatar
     */
    hideDefaultAvatar: { type: Boolean, default: false },
    showAvatar: { type: Boolean, default: true }
  },
  data() {
    return {
      isErrored: false
    }
  },
  computed: {
    photoUrl() {
      return getPhotoUrl(this.user && this.user.photoId)
    },
    initials() {
      return getInitials(this.user.firstName, this.user.lastName, this.user.email)
    },
    storedUser() {
      if (this.userId) {
        return this.$store.getters.userById(this.userId)
      } else {
        return null
      }
    },
    user() {
      return this.storedUser || { firstName: '', lastName: '', displayName: '' }
    }
  },
  methods: {
    async updateUser() {
      if (!this.storedUser && this.userId) {
        this.$store.dispatch('getUser', { id: this.userId })
      }
    },
    initialsBgColor(userId) {
      return PALETTE[userId % PALETTE.length]
    }
  },
  mounted() {
    this.updateUser()
  },
  watch: {
    userId: 'updateUser'
  }
}
</script>

<style scoped lang="scss">
.user-photo-container {
  &:not(.no-default-avatar) {
    background: url('../assets/default-avatar.svg') no-repeat center;
    background-size: contain;
  }
  background-size: cover;

  &.edit-view.no-user {
    border: 0.5px dashed #adadad;
  }

  flex-shrink: 0;
}

.truncate {
  overflow: hidden;
}

.user-initials {
  color: white;
  height: 100%;
  width: 100%;
}

.user-photo {
  width: 100%;
  height: 100%;
  vertical-align: baseline;
  flex-shrink: 0;
}

.user-short-container {
  display: inline-flex;
  align-items: center;
}
</style>
